"use client";

import { useEffect, useState } from "react";
import BFLLink from "@repo/shared-ui/BFLLink";
import BFLHoverBackdrop from "@repo/shared-ui/BFLHoverBackdrop";
import BFLImage from "@repo/shared-ui/BFLImage";
import {
  handleHeaderNotificationDeleteDL,
  handleHeaderNotificationBellDL,
  handleHeaderNotificationCloseDL,
} from "./HeaderAnalyticsFunction";

interface CTAItem {
  ctaInNewTab: string;
  ctaLink: string;
  ctaText: string;
}

interface NotificationItem {
  nodeName: string;
  imageAltText?: string;
  image?: string;
  cardLink?: string;
  children?: Array<{
    nodeName: string;
    children?: Array<CTAItem & { nodeName: string }>;
  }>;
  showDelete?: string;
  cardSubTitle?: string;
  cardTitle?: string;
  openInNewTab?: string;
}

interface CardListProps {
  notificationData: any;
  forceOpen?: boolean; // Optional prop to force panel open for testing
}

interface NotificationCard {
  title: string;
  subtitle: string;
  image: string;
  imageAlt: string;
  ctas: CTAItem[];
  showDelete: string;
  openInNewTab: string;
}

const Notification: React.FC<CardListProps> = ({
  notificationData,
  forceOpen,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(!!forceOpen);
  const [notificationCards, setNotificationCards] = useState<
    NotificationCard[]
  >([]);
  const getInitialDeletedTitles = () => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        return JSON.parse(
          window.localStorage.getItem("notiSessionData") ?? "[]",
        );
      } catch {
        return [];
      }
    }
    return [];
  };
  const [deletedTitles, setDeletedTitles] = useState<string[]>(
    getInitialDeletedTitles(),
  );

  const normalizeCtaData = (
    apiData: any,
  ): Array<{
    title: string;
    subtitle: string;
    image: string;
    imageAlt: string;
    ctas: CTAItem[];
    showDelete: string;
    openInNewTab: string;
  }> => {
    if (!Array.isArray(apiData?.children)) {
      return [];
    }

    // Handle items that start with 'item' as well as directly numbered items (item0, item1, etc.)
    return apiData.children
      .filter(
        (item: any) =>
          item.nodeName.startsWith("item") || /^item\d+$/.test(item.nodeName),
      )
      .map((item: any) => {
        // Find CTA List - handle both direct children and nested structures
        let ctaList = item.children?.find(
          (child: any) => child.nodeName === "ctaList",
        );
        let ctas: CTAItem[] = [];

        if (ctaList?.children) {
          ctas =
            ctaList.children.map((cta: any) => ({
              ctaText: cta.ctaText,
              ctaLink: cta.ctaLink,
              ctaInNewTab: cta.ctaInNewTab,
            })) || [];
        }

        return {
          title: item.cardTitle || "Notification",
          subtitle:
            item.cardSubTitle || "You have a new offer or product to explore.",
          image: item.image || "",
          imageAlt: item.imageAltText || "Notification Icon",
          ctas,
          showDelete: item.showDelete || "No",
          openInNewTab: item.openInNewTab || "false",
          cardLink: item.cardLink || "#",
        };
      });
  };

  useEffect(() => {
    const normalizedData = normalizeCtaData(notificationData);
    const filtered = normalizedData.filter(
      (card) => !deletedTitles.includes(card.title),
    );
    setNotificationCards(filtered);
  }, [notificationData, deletedTitles]);

  useEffect(() => {
    if (forceOpen) setIsOpen(true);
  }, [forceOpen]);

  const formatCtaLink = (ctaLink: string | undefined): string => {
    if (!ctaLink) return "#";

    if (ctaLink.startsWith("http")) {
      return ctaLink;
    }

    if (ctaLink.startsWith("/")) {
      return ctaLink;
    }

    return `/${ctaLink}`;
  };

  const deleteData = (index: number) => {
    const deletedTitle = notificationCards[index]?.title ?? "";
    const updatedList = notificationCards.filter(
      (_, idx: number) => idx !== index,
    );
    setNotificationCards(updatedList);

    const updatedDeleted = [...deletedTitles, deletedTitle];
    setDeletedTitles(updatedDeleted);
    localStorage.setItem("notiSessionData", JSON.stringify(updatedDeleted));
  };

  const AllNotifications = () =>
    notificationCards.length === 0 ? (
      <div className="py-4 text-center text-sm text-white">
        No new notifications.
      </div>
    ) : (
      <>
        {notificationCards.map((card, index: number) => (
          <div
            data-testid="notification-item"
            key={"notification-" + index}
            className="mb-2 flex flex-col items-start justify-start gap-3 rounded bg-white"
          >
            <div className="flex w-full items-start justify-between gap-3 px-3 pt-3">
              <div className="flex items-start gap-3">
                <BFLImage
                  src={
                    card.image ||
                    "https://cms-assets.bajajfinserv.in/is/image/bajajfinance/fd-max-9?scl=1&fmt=png-alpha"
                  }
                  width={30}
                  height={30}
                  alt={card.imageAlt || "Notification"}
                  priority={false}
                  loading="lazy"
                  unoptimized={true}
                />
                <div className="flex flex-col">
                  <h3 className="text-sm font-medium tracking-[0.16px] text-black">
                    {card.title}
                  </h3>
                  <p className="text-[12px] leading-[14px] text-gray-600">
                    {card.subtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Track delete notification analytics
                  handleHeaderNotificationDeleteDL("Notification delete icon");
                  deleteData(index);
                }}
                className="cursor-pointer text-red-500"
                aria-label="Delete notification"
              >
                <i className="icomoon text-sm before:content-['\e938']"></i>
              </button>
            </div>
            {card.ctas.length > 0 && (
              <div className="flex w-full items-center justify-between border-t border-gray-300 px-6 py-2.5 text-sm font-medium">
                {card.ctas.map((cta, idx: number) => (
                  <BFLLink
                    key={"notification-" + idx}
                    className="text-[12px] text-orange-500"
                    href={formatCtaLink(cta.ctaLink)}
                    openInNewTab={cta.ctaInNewTab === "true"}
                  >
                    {cta.ctaText}
                  </BFLLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </>
    );

  const handleMouseEnter = () => {
    if (window.innerWidth > 768) setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) setIsOpen(false);
  };

  const handleClick = () => {
    // Track notification bell icon click analytics
    handleHeaderNotificationBellDL("Notification bell icon");

    if (window.innerWidth <= 768) setIsOpen((prev) => !prev);
  };

  // Handle close button click analytics
  const handleCloseButtonClick = () => {
    // Track notification close icon click analytics
    handleHeaderNotificationCloseDL("Notification close icon");

    // Close the notification panel
    setIsOpen(false);
  };

  return (
    <div className="relative order-5 mr-0 inline-block md:order-3">
      <BFLHoverBackdrop zIndex={40} headerHeight={87}>
        <div
          className="group flex cursor-pointer flex-col items-center px-2 pb-[30px] pt-[14px] hover:bg-[#001326]"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          <i className="icomoon transform text-2xl transition-transform duration-300 before:content-['\e932'] md:!h-[26px]" />
          <span className="hidden items-center text-xs leading-[14px] md:!flex">
            Notifications{" "}
            <i className="icomoon ml-[2px] text-[10px] before:content-['\e981'] group-hover:-rotate-180" />
          </span>
        </div>

        {/* Desktop */}
        {(forceOpen || (isOpen && window.innerWidth > 768)) && (
          <>
            <div
              className="fixed left-0 top-[60px] z-40 h-[30px] w-full bg-opacity-50"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
            <div
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className="notification-scrollbar z-51 w-85 absolute left-1/2 top-20 hidden h-[90vh] -translate-x-1/2 transform overflow-auto rounded-lg bg-[#002142] py-2 pl-4 pr-3 shadow-lg md:!block md:w-[380px]"
            >
              <AllNotifications />
            </div>
          </>
        )}
      </BFLHoverBackdrop>
      {/* Mobile */}
      {(forceOpen || (isOpen && window.innerWidth <= 768)) && (
        <div className="fixed inset-0 z-50 block bg-white md:!hidden">
          <div className="relative bg-[#001E50] py-4 text-center text-sm font-medium">
            Notifications
            <button
              onClick={handleCloseButtonClick}
              className="absolute left-4 top-4"
              aria-label="Close notifications"
            >
              X
            </button>
          </div>
          <div className="h-[calc(100%-64px)] space-y-4 overflow-y-auto bg-[#d3dae1] p-4">
            <AllNotifications />
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;
